import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Calendar,
  Check,
  Clock,
  Film,
  Layers,
  Link as LinkIcon,
  Scissors,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";
import Footer from "../Components/footer";
import { useAuth } from "../Context/AuthContext";
import { subscribeOwnedCampaigns } from "../services/clippingCampaigns";
import { CAMPAIGN_STATUSES } from "../utils/clippingCampaignDomain";

const NEON = "#84ff00";
const PURPLE = "#9146FF";
const PURPLE_DEEP = "#7a3ae7";
const BG = "#07060d";
const SURFACE = "rgba(255,255,255,0.04)";
const HAIRLINE = "rgba(255,255,255,0.08)";
const GRID_BG =
  "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)";

function getAccountRole(userData) {
  return String(userData?.profileInfo?.role || userData?.role || "")
    .trim()
    .toLowerCase();
}

function isCreatorAccount(userData) {
  return ["creator", "streamer"].includes(getAccountRole(userData));
}

function hasCampaignOperationsAccess(userData) {
  return isCreatorAccount(userData);
}

function Section({ children, className = "" }) {
  return (
    <section className={`mx-auto w-full max-w-7xl px-4 py-16 sm:px-8 sm:py-28 ${className}`}>
      {children}
    </section>
  );
}

function CTAButton({ to, variant = "primary", children }) {
  const base =
    "group inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 sm:w-auto sm:px-7";

  if (variant === "primary") {
    return (
      <Link
        to={to}
        className={`${base} text-black`}
        style={{
          background: NEON,
          boxShadow: `0 10px 40px ${NEON}40`,
        }}
      >
        {children}
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className={`${base} text-white`}
      style={{
        background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})`,
        boxShadow: `0 10px 40px ${PURPLE}55, inset 0 1px 0 rgba(255,255,255,.15)`,
      }}
    >
      {children}
    </Link>
  );
}

function TrustBullet({ children }) {
  return (
    <li className="flex items-center gap-2 text-sm text-zinc-400">
      <Check className="h-4 w-4 shrink-0" style={{ color: NEON }} />
      <span>{children}</span>
    </li>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`min-w-0 rounded-2xl border border-white/10 p-5 backdrop-blur-xl sm:p-6 ${className}`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        boxShadow:
          "0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03)",
      }}
    >
      {children}
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <Card className="h-full transition-colors hover:border-white/15">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: "rgba(132,255,0,0.1)" }}
      >
        <Icon className="h-5 w-5" style={{ color: NEON }} />
      </div>
      <h3 className="mt-5 text-xl font-semibold text-zinc-100">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{children}</p>
    </Card>
  );
}

function StepCard({ n, title, description, icon: Icon, accent = "neon" }) {
  const color = accent === "purple" ? PURPLE : NEON;

  return (
    <div
      className="flex flex-col rounded-xl border p-6 transition-colors hover:border-white/15"
      style={{
        backgroundColor: SURFACE,
        borderColor: `${color}33`,
        boxShadow: `0 20px 60px -40px ${color}50`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
          style={{ backgroundColor: color, color: "#0a0f0d" }}
        >
          {n}
        </div>
        <span className="text-xs font-medium uppercase tracking-wider" style={{ color }}>
          Step {n}
        </span>
      </div>
      <div
        className="mt-5 flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-zinc-100">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{description}</p>
    </div>
  );
}

export default function ClippingCampaigns() {
  const { currentUser, activeUser } = useAuth();
  const [ownedCampaigns, setOwnedCampaigns] = useState([]);
  const isStreamer = isCreatorAccount(activeUser);
  const hasCampaignOperations = hasCampaignOperationsAccess(activeUser);
  useEffect(() => subscribeOwnedCampaigns(currentUser?.uid, setOwnedCampaigns, () => {}), [currentUser?.uid]);
  const editableCampaign = ownedCampaigns.find((campaign) => campaign.status === CAMPAIGN_STATUSES.DRAFT);
  const primaryCampaign = editableCampaign || ownedCampaigns[0];
  const ownerAction = editableCampaign
    ? { label: "Edit Campaign", to: `/clipping-campaigns/${editableCampaign.id}/edit` }
    : primaryCampaign
      ? { label: "Manage Campaign", to: `/clipping-campaigns/${primaryCampaign.id}` }
      : { label: "Create Campaign", to: "/clipping-campaigns/new" };

  return (
    <>
      <SEO
        title="Clipping Campaigns for Twitch and Kick Streamers | Kre8"
        description="Twitch and Kick streamers can create clipping campaign drafts, while clippers discover short form opportunities for TikTok, Shorts, and Reels."
        canonicalPath="/clipping-campaigns"
        structuredData={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Clipping Campaigns", path: "/clipping-campaigns" },
        ])}
      />
      <TheNav />
      <main className="mobile-safe min-h-screen antialiased" style={{ backgroundColor: BG, color: "#e5e7eb" }}>
        <Section className="relative overflow-hidden !pt-16 sm:!pt-28">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-80"
            style={{
              background: `radial-gradient(60% 50% at 20% 20%, ${PURPLE}33, transparent 60%), radial-gradient(50% 50% at 85% 30%, ${NEON}1a, transparent 60%), radial-gradient(40% 40% at 50% 90%, ${PURPLE_DEEP}33, transparent 60%)`,
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-[0.06]"
            style={{
              backgroundImage: GRID_BG,
              backgroundSize: "60px 60px",
              maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            }}
          />

          <div className="relative mx-auto max-w-4xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium"
              style={{
                borderColor: `${NEON}55`,
                color: NEON,
                background: `${NEON}10`,
                boxShadow: `0 0 24px ${NEON}22`,
              }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Early access for clipping campaigns
            </p>
            <h1 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-5xl md:text-6xl md:leading-[1.05]">
              Turn Twitch and Kick streams into short form growth
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-zinc-400 sm:mt-6 sm:text-xl">
              Kre8 clipping campaigns are being built for streamers who want more clips from every stream and clippers who want paid view based campaign opportunities.
            </p>
            <ul className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-x-6 gap-y-3">
              <TrustBullet>Creators set the RPM per 1,000 views</TrustBullet>
              <TrustBullet>Campaigns for TikTok, Shorts, and Reels</TrustBullet>
              <TrustBullet>Tracking windows for 48 hours, 7 days, and 30 days</TrustBullet>
            </ul>
            <div className="mt-10 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:justify-center">
              <CTAButton to="/clipping-campaigns/discover">Discover Campaigns</CTAButton>
              {(!currentUser || isStreamer) && (
                <CTAButton to="/launch-clipping-campaign" variant="secondary">
                  Prepare a Campaign
                </CTAButton>
              )}
              <CTAButton to="/clipping-campaigns/dashboard" variant="secondary">
                Open Dashboard
              </CTAButton>
              {hasCampaignOperations && (
                <CTAButton to="/clipping-campaigns/admin" variant="secondary">
                  Campaign Operations
                </CTAButton>
              )}
            </div>
          </div>
        </Section>

        <Section className="!pt-0">
          <div className="grid gap-5 lg:grid-cols-3">
            <InfoCard icon={Users} title="For streamers">
              Open a campaign around your streams so clippers know what moments, formats, and platforms matter most.
            </InfoCard>
            <InfoCard icon={Scissors} title="For clippers">
              Join early, build your profile, and be ready when streamer campaigns start opening on Kre8.
            </InfoCard>
            <InfoCard icon={TrendingUp} title="For growth">
              More people cutting stream moments means more chances to find clips that actually travel.
            </InfoCard>
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>
              How campaigns will work
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Built around views, RPM, and clear tracking windows
            </h2>
            <p className="mt-4 text-zinc-400">
              The creator sets the RPM, campaign budget, link submission window, tracking cap, and whether duplicate posts across platforms are all paid or only the top performer is paid.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            <StepCard
              n="1"
              title="Streamer sets the RPM"
              description="Choose the price per 1,000 views, campaign budget, target platforms, and the rules for counting views."
              icon={Layers}
            />
            <StepCard
              n="2"
              title="Clippers post and submit links"
              description="Clippers publish their clips and submit links before the campaign submission window closes."
              icon={Scissors}
              accent="purple"
            />
            <StepCard
              n="3"
              title="Views turn into payout estimates"
              description="Campaigns can check early growth, 7 day performance, and a 30 day cap before payouts are finalized."
              icon={Film}
            />
          </div>
        </Section>

        <Section>
          <div className="grid gap-5 lg:grid-cols-4">
            <InfoCard icon={BarChart3} title="RPM">
              Creators set a price per 1,000 views so clippers know what the campaign pays before they join.
            </InfoCard>
            <InfoCard icon={Clock} title="Submit links fast">
              Creators can set how long clippers have to submit posted clip links, like 24 or 48 hours after posting.
            </InfoCard>
            <InfoCard icon={Calendar} title="Tracking cap">
              Campaigns can use a 7 day check for early payouts and a 30 day cap for final view tracking.
            </InfoCard>
            <InfoCard icon={LinkIcon} title="Platform rule">
              Creators can pay views across every platform or only pay the version with the most views.
            </InfoCard>
          </div>
        </Section>

        <Section>
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 p-10 text-center sm:p-16"
            style={{
              background: `linear-gradient(135deg, ${PURPLE}55, #0a0a14 50%, ${NEON}33)`,
              boxShadow: `0 30px 100px rgba(0,0,0,0.6), 0 0 100px ${PURPLE}33`,
            }}
          >
            <BadgeCheck className="mx-auto h-10 w-10" style={{ color: NEON }} />
            <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-5xl">
              Get in before campaigns fully open
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-zinc-300">
              Join early so your profile is ready when clipping campaigns begin rolling out to more Twitch and Kick streamers.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <CTAButton to="/clipping-campaigns/discover">Browse Live Campaigns</CTAButton>
              {(!currentUser || isStreamer) && <CTAButton to={ownerAction.to} variant="secondary">
                {ownerAction.label}
              </CTAButton>}
            </div>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
